import { AppBar, Box, Dialog, DialogContent, Stack, Toolbar } from "@mui/material";
import { AppContext } from "../../common/AppContext";
import { Utils } from "../../common/Utils";
import { Alert } from "../../components/Alert";
import { Button } from "../../components/Button";
import { TextField } from "../../components/TextField";
import { ViewComponent } from "../../components/ViewComponent";
import { withLocale, WithLocaleProps } from "../../providers/LocaleProvider";
import CloseIcon from "../../resources/images/icons-close.svg";
import PlaneIcon from "../../resources/images/icons-plane.svg";
import Logo from "../../resources/images/logo.svg";

import axiosClient from "../../common/AxiosClient";
import { ConfirmationBox } from "../../components/ConfirmationBox";

interface ContactUsDialogProps extends WithLocaleProps {
  user?: any;
  organization?: any;
  theme?: any;
  redirect?: string;
  onClose?: () => void;
}

class ContactUsDialogBase extends ViewComponent<ContactUsDialogProps> {
  static contextType = AppContext;

  constructor(props: any) {
    super(props);

    this.state = {
      firstName: props.user ? props.user.firstName : "",
      lastName: props.user ? props.user.lastName : "",
      email: props.user ? props.user.email : "",
      phone: "",
      company: props.organization ? props.organization.name : "",
      numUsersRequired: "",
      message: "",
      error: null,
      sending: false,
      sent: false
    };
  }

  onFirstNameChange(e: any) {
    this.setState({ firstName: e.target.value });
  }

  onLastNameChange(e: any) {
    this.setState({ lastName: e.target.value });
  }

  onEmailChange(e: any) {
    this.setState({ email: e.target.value });
  }

  onPhoneChange(e: any) {
    this.setState({ phone: e.target.value });
  }

  onCompanyChange(e: any) {
    this.setState({ company: e.target.value });
  }

  onNumUsersChange(e: any) {
    this.setState({ numUsersRequired: e.target.value });
  }

  onMessageChange(e: any) {
    this.setState({ message: e.target.value });
  }

  validate() {
    const { translate } = this.props;
    const { firstName, lastName, email, phone, company, numUsersRequired, message, error } =
      this.state;

    if (!firstName) {
      this.setError(translate("contact.errorFirstName"));
      return false;
    } else if (!lastName) {
      this.setError(translate("contact.errorLastName"));
      return false;
    } else if (!Utils.isValidEmail(email)) {
      this.setError(translate("contact.errorEmail"));
      return false;
    } else if (!company) {
      this.setError(translate("contact.errorCompany"));
      return false;
    } else if (!phone) {
      this.setError(translate("contact.errorPhone"));
      return false;
    } else if (!numUsersRequired) {
      this.setError(translate("contact.errorNumUsers"));
      return false;
    }
    return true;
  }

  setError(error: any) {
    this.setState({ error });
  }

  onSend(e: any) {
    if (!this.validate()) return;

    this.setState({ error: null, sending: true });
    this.context.setLoading?.("sending", true);

    const {
      firstName,
      lastName,
      email,
      phone,
      company,
      numUsersRequired,
      message,
      error,
      sending
    } = this.state;

    axiosClient
      .post(
        "/Organization/ContactUs",
        {
          firstName: firstName,
          lastName: lastName,
          email: email,
          phone: phone,
          companyName: company,
          numberOfUserRequired: numUsersRequired,
          message: message
        },
        {
          withCredentials: true
        }
      )
      .then((response) => {
        this.setState({ sent: true, error: null, sending: false });
      })
      .finally(() => {
        this.context.setLoading?.("sending", false);
        this.setState({ sending: false });
      });
  }

  render() {
    const { organization, user, onClose, translate } = this.props;
    const {
      firstName,
      lastName,
      email,
      phone,
      company,
      numUsersRequired,
      message,
      error,
      sending,
      sent
    } = this.state;

    return (
      <Dialog className='klayo-billingplandialog' onClose={onClose} open={true} fullScreen={true}>
        <AppBar className='klaro_employeeprofile-appbar' elevation={0}>
          <Toolbar>
            <Logo className='klaro-appbar_logo' />
            <Box sx={{ flexGrow: 1 }}></Box>
            <Box sx={{ display: { xs: "none", md: "flex" } }}>
              <Button
                autoFocus
                label={translate("common.close")}
                variant='outlined'
                size='md'
                startIcon={<CloseIcon />}
                onClick={onClose}
              />
            </Box>
          </Toolbar>
        </AppBar>
        <DialogContent sx={{ marginTop: "64px", backgroundColor: "#F8F8FE" }}>
          <div className='klayo-billingplandialog_content'>
            {sent && (
              <ConfirmationBox
                icon={<PlaneIcon />}
                heading={translate("contact.gotIt")}
                message={translate("contact.inTouchShortly")}
                action={translate("common.close")}
                onAction={onClose}
              />
            )}

            {!sent && (
              <Stack direction='column' spacing={2}>
                <h1>{translate("contact.title")}</h1>
                <Stack spacing={5}>
                  {error && <Alert severity='error'>{error}</Alert>}

                  <Stack direction='row' spacing={4} justifyContent='space-between'>
                    <TextField
                      label={translate("labels.firstName")}
                      value={firstName}
                      autoFocus={true}
                      clearable={false}
                      autoComplete={false}
                      onChange={this.onFirstNameChange.bind(this)}
                      sx={{ width: "100%" }}
                    />
                    <TextField
                      label={translate("labels.lastName")}
                      value={lastName}
                      clearable={false}
                      autoComplete={false}
                      onChange={this.onLastNameChange.bind(this)}
                      sx={{ width: "100%" }}
                    />
                  </Stack>

                  <Stack direction='row' spacing={4} justifyContent='space-between'>
                    <TextField
                      label={translate("labels.email")}
                      value={email}
                      clearable={false}
                      autoComplete={false}
                      onChange={this.onEmailChange.bind(this)}
                      sx={{ width: "100%" }}
                    />
                    <TextField
                      label={translate("labels.phone")}
                      value={phone}
                      clearable={false}
                      autoComplete={false}
                      onChange={this.onPhoneChange.bind(this)}
                      sx={{ width: "100%" }}
                    />
                  </Stack>

                  <Stack direction='row' spacing={4} justifyContent='space-between'>
                    <TextField
                      label={translate("labels.companyName")}
                      value={company}
                      clearable={false}
                      autoComplete={false}
                      onChange={this.onCompanyChange.bind(this)}
                      sx={{ width: "100%" }}
                    />
                    <TextField
                      label={translate("contact.numberOfUsers")}
                      value={numUsersRequired}
                      clearable={false}
                      autoComplete={false}
                      onChange={this.onNumUsersChange.bind(this)}
                      sx={{ width: "100%" }}
                    />
                  </Stack>

                  <Stack direction='row' spacing={4} justifyContent='space-between'>
                    <TextField
                      label={translate("contact.optionalMessage")}
                      multiline={true}
                      minRows={4}
                      value={message}
                      clearable={false}
                      autoComplete={false}
                      onChange={this.onMessageChange.bind(this)}
                      sx={{ width: "100%" }}
                    />
                  </Stack>

                  <div style={{ textAlign: "center", padding: "30px" }}>
                    <Button
                      label={translate("contact.sendDetails")}
                      variant='filled'
                      disabled={sending}
                      onClick={this.onSend.bind(this)}
                    />
                  </div>
                </Stack>
              </Stack>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  }
}

export const ContactUsDialog = withLocale(ContactUsDialogBase);
